import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { Mail, Lock, User, Phone, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function CustomerLoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const { loginCustomer, registerCustomer } = useCustomerAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await loginCustomer(form.email, form.password);
        toast.success("Welcome back!");
      } else {
        if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); setLoading(false); return; }
        await registerCustomer(form.email, form.password, form.name, form.phone);
        toast.success("Account created!");
      }
      navigate("/account");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong");
    }
    setLoading(false);
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-12 px-4" data-testid="customer-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <h1 className="text-4xl font-black tracking-tight">UKOK</h1>
          </Link>
          <p className="text-gray-500 mt-2">{isLogin ? "Sign in to your account" : "Create your account"}</p>
        </div>

        <div className="border-2 border-[#0A0A0A] bg-white p-8">
          {/* Google Login */}
          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 border-2 border-[#E5E5E5] p-3 hover:bg-[#F5F5F5] transition-colors mb-6"
            data-testid="google-login-button"
          >
            <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            <span className="font-bold">Continue with Google</span>
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 border-t border-[#E5E5E5]" />
            <span className="text-xs text-gray-400 uppercase font-bold">Or</span>
            <div className="flex-1 border-t border-[#E5E5E5]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <Label className="font-bold text-sm">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Your full name" className="pl-10" data-testid="register-name-input" />
                  </div>
                </div>
                <div>
                  <Label className="font-bold text-sm">Phone Number</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+234..." className="pl-10" data-testid="register-phone-input" />
                  </div>
                </div>
              </>
            )}
            <div>
              <Label className="font-bold text-sm">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="you@example.com" className="pl-10" data-testid="login-email-input" />
              </div>
            </div>
            <div>
              <Label className="font-bold text-sm">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required placeholder={isLogin ? "Your password" : "Min 6 characters"} className="pl-10" data-testid="login-password-input" />
              </div>
            </div>
            <Button type="submit" className="btn-primary w-full h-12 text-base" disabled={loading} data-testid="login-submit-button">
              {loading ? <div className="spinner w-5 h-5" /> : (
                <span className="flex items-center justify-center gap-2">{isLogin ? "Sign In" : "Create Account"} <ChevronRight className="w-4 h-4" /></span>
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
            <button onClick={() => setIsLogin(!isLogin)} className="text-[#FF6B00] font-bold hover:underline" data-testid="toggle-auth-mode">
              {isLogin ? "Sign Up" : "Sign In"}
            </button>
          </p>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          By continuing, you agree to our <Link to="/terms" className="underline">Terms & Conditions</Link>
        </p>
      </div>
    </div>
  );
}
